import { maskVendor } from "@/shared/lib/vendorMask";
import {
  Search,
  Plus,
  Filter,
  Eye,
  Users,
  RefreshCw,
  Loader2,
  Trash2,
  BookOpen,
  Tag,
  Download,
  EyeOff,
  Info,
  ShieldCheck,
  X,
} from "lucide-react";
import { Card, CardContent } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Pagination } from "@/shared/ui/pagination";
import { useClampedPage } from "@/shared/hooks/useClampedPage";
import { Input } from "@/shared/ui/input";
import { Badge } from "@/shared/ui/badge";
import { StatusBadge } from "@/shared/ui/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/shared/ui/tabs";
import { Label } from "@/shared/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form";
import { ChangeEvent, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/ui/tooltip";
import { RadioGroup, RadioGroupItem } from "@/shared/ui/radio-group";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { useUrlParams } from "@/shared/hooks/useUrlParams";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/shared/hooks/use-confirm";
import {
  type KbVisibleScope,
  kbFailureText,
  knowledgeService,
  type KnowledgeDocument,
  type KnowledgeDocType,
} from "@/entities/knowledge";
import { toast } from "@/shared/hooks/use-toast";
import { CoverageInsightPanel } from "./CoverageInsightPanel";
import { TagManagementPanel } from "./TagManagementPanel";
import { PersonalDocsPanel } from "./PersonalDocsPanel";
import { TagSelector, tagService } from "@/entities/tag";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { isTenantAdminPlus, isTenantManagerPlus } from "@/shared/lib/rbac";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { productService } from "@/entities/product";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import {
  batchRevocableIds,
  canGrantCustomerVisible,
  filterByVisibility,
  isVisibilityAlarming,
  summarizeVisibility,
  VISIBILITY_FILTER_LABEL,
  type VisibilityFilter,
  canRevokeCustomerVisible,
  INVISIBLE_REASON_LABEL,
  invisibleReason,
  isCustomerVisible,
  visibilityErrorToast,
} from "../lib/kbVisibility";
import { kbDocReviewService } from "@/entities/knowledge/api/kbDocReviewService";
import { Checkbox } from "@/shared/ui/checkbox";

// 2026-06-04 — 上传时可选「所属产品」, "__none__" 哨兵 = 通用(不绑产品树)。
import { kbProposeReviewerHint, kbUploadMode } from "../lib/kbUploadGate";
import { partitionTenantDocUploads, TENANT_DOC_MAX_MB } from "../lib/tenantDocsUploadLimits";
import { VISIBLE_SCOPE_LABEL } from "../lib/kbVisibleScope";
import { KbVisibleScopeField } from "./KbVisibleScopeField";
import { usePersona } from "@/shared/brand/persona";

const NODE_NONE = "__none__";

const KNOWLEDGE_STATUS_TYPES: Record<string, "success" | "warning" | "info" | "error"> = {
  done: "success",
  processing: "warning",
  pending: "info",
  failed: "error",
};

const PAGE_SIZE = 10;

// V27 (2026-05-20) — 代客上传时, 用户从已注册字典里挑 tag(逗号分隔 tagValue)。
// 后端会再过一遍字典, 不在的静默丢; 不会自动新建 tag。
const uploadFormSchema = z.object({
  tags: z.string(),
  docType: z.enum(["product", "qa"]),
  // 2026-06-04 — 所属产品节点 code,默认 NODE_NONE(通用)。
  nodeCode: z.string(),
});

type UploadFormValues = z.infer<typeof uploadFormSchema>;

export default function KnowledgeBase() {
  const persona = usePersona();
  const { t } = useTranslation("kb");
  const confirm = useConfirm();
  const statusLabel = (status: string): string =>
    t(`knowledgeBase.status.${status}`, { defaultValue: status });
  const queryClient = useQueryClient();
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  // V40 (2026-05-19) — kbSearch + page 上 URL, 修浏览器回退丢搜索/页码。
  // 详见 docs/superpowers/plans/2026-05-19-list-state-url-binding.md。
  const { sp, update } = useUrlParams();
  const kbSearch = sp.get("q") ?? "";
  const page = Math.max(1, Number(sp.get("page") ?? "1") || 1);
  const setKbSearch = (v: string) => update({ q: v || null, page: null }, { replace: true });
  const setPage = (p: number) => update({ page: p <= 1 ? null : p });

  const [viewingDoc, setViewingDoc] = useState<KnowledgeDocument | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  // 2026-09-16 对内可见范围:默认全公司 —— 知识库的本意就是共享,收窄是例外而不是默认,
  // 所以绝大多数上传零操作。
  const [uploadScope, setUploadScope] = useState<KbVisibleScope>("ALL");
  const [uploadDeptId, setUploadDeptId] = useState<number | null>(null);
  // 行内改档:开一个小弹窗复用同一个选择器 —— 下拉直接改会让「选错部门」变成一次静默的越权,
  // 而这个动作是要落审计的。
  const [scopeEditing, setScopeEditing] = useState<KnowledgeDocument | null>(null);
  const [editScope, setEditScope] = useState<KbVisibleScope>("ALL");
  const [editDeptId, setEditDeptId] = useState<number | null>(null);
  // 2026-06-04 — 多选上传:从单 File 改 File[]。提交时逐个串行调 uploadDocument,
  // 共用同一份 nodeCode/docType/tags(一个产品归属、一批文件)。
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  // 串行上传进度:null = 未在上传;否则 {done, total} 用于「上传中 N/M …」。
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(
    null
  );
  const isUploading = uploadProgress !== null;

  // D6:上传口按**运行时身份**分两种(见 lib/kbUploadGate)——
  //   代客态 = 平台超管本人 → 直传生效;租户 MANAGER+ → 提名(落待审)。
  // 此前是 canManage=isImpersonating 一刀切关死租户,那条封条是**因为没有治理门**才贴的;
  // 门建好之后再关着,tenant_proposal 渠道在生产里就没有任何 UI 入口。
  // canManage(标签 CRUD / 绑产品树 / 全量删除)仍只给代客态;K-2 起租户 MANAGER+ 可删自己未过审的文档(行内另判)。
  const { isImpersonating } = useImpersonation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = isImpersonating;
  const uploadMode = kbUploadMode(isImpersonating, user?.role);
  // 1005:按钮/弹窗标题/空态三处同一个词 —— 此前代客态空态写「点击『上传文档』」而按钮叫「代客上传文档」
  const uploadButtonLabel = uploadMode === "direct" ? "代客上传文档" : "上传文档(待审)";
  const canUpload = uploadMode !== "none";

  const uploadForm = useForm<UploadFormValues>({
    resolver: zodResolver(uploadFormSchema),
    mode: "onBlur",
    defaultValues: { tags: "", docType: "product", nodeCode: NODE_NONE },
  });

  // 2026-06-04 — 产品树选项, 仅代客登录(canManage)时拉取, 与上传同门控。
  const { data: productData } = useQuery({
    queryKey: ["product-nodes"],
    queryFn: productService.list,
    enabled: isImpersonating,
  });
  const productNodes = productData ?? [];

  const { data: documents = [], isLoading: documentsLoading } = useQuery({
    queryKey: ["knowledge-documents"],
    queryFn: knowledgeService.listDocuments,
  });

  const { data: tagDefs = [] } = useQuery({
    queryKey: ["kb-tags"],
    queryFn: () => tagService.list(),
  });

  const tagNameMap = Object.fromEntries(tagDefs.map((t) => [t.tagValue, t.name]));

  const resetUploadState = () => {
    setUploadDialogOpen(false);
    uploadForm.reset({ tags: "", docType: "product", nodeCode: NODE_NONE });
    setPendingFiles([]);
    if (uploadInputRef.current) uploadInputRef.current.value = "";
  };

  // 2026-06-04 — 多文件串行上传:共用同一 nodeCode/docType/tags,逐个 await,
  // 统计成功/失败,完成后失效列表 query 并按结果 toast。
  const runSequentialUpload = async (
    files: File[],
    args: {
      tags?: string;
      docType: KnowledgeDocType;
      nodeCode?: string;
      visibleScope?: KbVisibleScope;
      visibleDeptId?: number | null;
    }
  ) => {
    setUploadProgress({ done: 0, total: files.length });
    const failures: string[] = [];
    const failReasons = new Set<string>();
    let successCount = 0;
    const warnings: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ done: i, total: files.length });
      try {
        const resp = await knowledgeService.uploadDocument(
          file,
          args.tags,
          args.docType,
          args.nodeCode,
          null,
          args.visibleScope,
          args.visibleDeptId
        );
        // ai 对「解析出 0 chunk」返回 HTTP 200 + status:"failed"(如 doc_type=qa 但
        // 文件无 Q&A 对)。必须看 status,否则会把 failed 误当成功计数、丢掉真实原因。
        if (resp?.status === "failed") {
          failures.push(file.name);
          const reason = maskVendor(resp.error?.trim());
          if (reason) failReasons.add(reason);
        } else {
          successCount++;
          if (resp?.warnings && resp.warnings.length > 0) {
            resp.warnings.forEach((w) => warnings.push(`${file.name}: ${w}`));
          }
        }
      } catch (e) {
        // ★0921:这里原来是裸 `catch {}` —— 把错误**整个扔掉**,于是无论后端说了什么
        //   (「文档过大(上限 50MB)」、413 那句「文件太大…」、权限不足…),用户拿到的
        //   永远只有一句「上传失败」+ 文件名。失败原因本来就在手上,不给才是缺陷。
        failures.push(file.name);
        const reason = e instanceof Error ? e.message.trim() : "";
        if (reason) failReasons.add(reason);
      }
    }

    setUploadProgress(null);
    queryClient.invalidateQueries({ queryKey: ["knowledge-documents"] });

    warnings.forEach((w) => toast({ title: t("knowledgeBase.toast.parseHint"), description: w }));

    if (failures.length === 0) {
      toast({
        title: t("knowledgeBase.toast.uploadSuccess"),
        description: t("knowledgeBase.toast.uploadBatchSummary", {
          defaultValue: "成功 {{success}}/{{total}} 个文件",
          success: successCount,
          total: files.length,
        }),
      });
      resetUploadState();
    } else {
      // 列前几个失败文件名,避免 toast 过长。
      const preview = failures.slice(0, 3).join("、");
      const more =
        failures.length > 3
          ? t("knowledgeBase.toast.uploadFailMore", {
              defaultValue: "等 {{count}} 个",
              count: failures.length,
            })
          : "";
      // 把 ai 的具体失败原因(去重,最多 2 条)带进 toast,例如
      //「未识别出任何 Q&A 对，请检查格式（Q:/A: 或 问：/答：）」,引导用户改文档类型/格式。
      const reasonText =
        failReasons.size > 0
          ? t("knowledgeBase.toast.uploadFailReason", {
              defaultValue: "；原因:{{reasons}}",
              reasons: Array.from(failReasons).slice(0, 2).join("；"),
            })
          : "";
      toast({
        title: t("knowledgeBase.toast.uploadFailed"),
        description: t("knowledgeBase.toast.uploadBatchFail", {
          defaultValue: "成功 {{success}}/{{total}},失败:{{names}}{{more}}{{reason}}",
          success: successCount,
          total: files.length,
          names: preview,
          more: more ? ` ${more}` : "",
          reason: reasonText,
        }),
        variant: "destructive",
      });
      // 失败时不关弹窗,保留已选(用户可重试),仅清空成功的留失败的不可行(无逐文件态),
      // 故保留全部选择,用户可手动移除已成功的或整体重试。
    }
  };

  const deleteMutation = useMutationWithToast({
    mutationFn: (id: number) => knowledgeService.deleteDocument(id),
    successToast: t("knowledgeBase.toast.deleteSuccess"),
    errorToast: t("knowledgeBase.toast.deleteFailed"),
    invalidateKeys: [["knowledge-documents"]],
  });

  const downloadMutation = useMutationWithToast({
    mutationFn: ({ id, filename }: { id: number; filename: string }) =>
      knowledgeService.downloadDocument(id, filename),
    errorToast: t("knowledgeBase.toast.downloadFailed", { defaultValue: "下载失败" }),
  });

  const scopeMutation = useMutationWithToast({
    mutationFn: ({
      id,
      scope,
      deptId,
    }: {
      id: number;
      scope: KbVisibleScope;
      deptId: number | null;
    }) => knowledgeService.setVisibleScope(id, scope, deptId),
    successToast: "已更新公司内可见范围",
    errorToast: "更新可见范围失败",
    onSuccess: () => {
      setScopeEditing(null);
      void queryClient.invalidateQueries({ queryKey: ["knowledge-documents"] });
    },
  });

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    // 体积预检放在**选文件当场**(0921):0921 17:58 一个 158MB 的 PDF 连传两次,
    // 两次都被边缘 nginx 的 100m 墙回 413(请求没到 backend),用户只看到「上传失败」。
    // 与其让他把 multipart 传完再挨一刀,不如选完当场说清「哪一份、超了多少的上限」。
    const { accepted, oversized } = partitionTenantDocUploads(files);
    if (oversized.length > 0) {
      toast({
        variant: "destructive",
        title: `有 ${oversized.length} 份文件超过 ${TENANT_DOC_MAX_MB}MB,未加入本次上传`,
        description: oversized
          .slice(0, 3)
          .map((f) => f.name)
          .join("、"),
      });
    }
    // 清空 input value,使再次选「同一批」文件也能触发 onChange。
    if (uploadInputRef.current) uploadInputRef.current.value = "";
    if (accepted.length === 0) return;
    setPendingFiles(accepted);
    setUploadDialogOpen(true);
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadConfirm = uploadForm.handleSubmit((values) => {
    if (pendingFiles.length === 0 || isUploading) return;
    void runSequentialUpload(pendingFiles, {
      tags: values.tags || undefined,
      docType: values.docType,
      nodeCode: values.nodeCode && values.nodeCode !== NODE_NONE ? values.nodeCode : undefined,
      visibleScope: uploadScope,
      visibleDeptId: uploadDeptId,
    });
  });

  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>("all");
  // 批量只做「收回对客可见」——放开的风险不对称,见 kbVisibility.batchRevocableIds 的注释。
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [revoking, setRevoking] = useState(false);

  const totalDocs = documents.length;
  // ★ 2026-08-07 事故防线:客服到底看得见几份。当天 19 份文档全绿而客服检索面是空的,
  //   租户被蒙了两小时 —— 界面上只要有这一行,配完第一眼就发现了。
  const visibilitySummary = summarizeVisibility(documents);
  const visibilityAlarming = isVisibilityAlarming(visibilitySummary);
  const totalChunks = documents.reduce((sum, d) => sum + (d.chunkCount ?? 0), 0);
  const syncedDocs = documents.filter((d) => d.status === "done").length;

  const filteredDocs = filterByVisibility(
    documents.filter(
      (doc) => !kbSearch || (doc.filename ?? "").toLowerCase().includes(kbSearch.toLowerCase())
    ),
    visibilityFilter
  );

  const revocableSelected = batchRevocableIds(filteredDocs, selectedIds);

  /**
   * 放开单份「对客可见」(2026-08-08 补)。
   *
   * ★这个动作此前**全站没有入口**:`approve` 只翻 `reviewStatus` 那一根,而 AI 客服的闸门要
   * `public ∧ approved` 两根同时满足 —— 于是租户自己传的文档审完了也永远停在 `internal`,
   * 行上那个「仅内部」黄标显示得好好的,就是点不动。
   *
   * ★逐份放、且要确认:与批量收回刻意不对称(见 `batchRevocableIds` 的注释)——
   * 放开的失败形态是「内部内容被讲给客户」,话说出去收不回来。
   * 后端权限是 `TENANT_ADMIN`(代客态走平台口),所以按钮也照这个门给,别亮着骗人。
   */
  const canGrantVisibility = isTenantAdminPlus(user?.role);
  // 1006:提名态横幅「谁来审」按单管理员自审口径如实说(与审核台共用同一个 query key)
  const { data: reviewPolicy } = useQuery({
    queryKey: ["kb-doc-review", "policy"],
    queryFn: () => kbDocReviewService.reviewPolicy(),
    enabled: uploadMode === "propose" && canGrantVisibility,
    staleTime: 60_000,
  });
  // 改「公司内可见范围」= MANAGER+,与后端 @PreAuthorize 同门;员工看得到但改不了。
  const canChangeScope = isImpersonating || isTenantManagerPlus(user?.role);
  const grantVisibility = useMutationWithToast({
    mutationFn: (id: number) =>
      kbDocReviewService.markVisibility(id, "public", { note: "设为对客可见" }),
    successToast: "已设为对客可见 — AI 客服现在检索得到它了",
    errorToast: visibilityErrorToast("设置失败(未生效,可重试)"),
    invalidateKeys: [["knowledge-documents"], ["kb-doc-review"]],
  });
  /**
   * 逐份收回对客可见(B2 #21,0927 K2):列表上 public 行此前状态 / 操作列都是空的 —— 「已对客」既无徽章也无入口收回,
   * 只能去审批台或批量勾选。收回是安全阀(见 kbVisibility.canRevokeCustomerVisible),不再二次确认。
   */
  const revokeVisibilityOne = useMutationWithToast({
    mutationFn: (id: number) =>
      kbDocReviewService.markVisibility(id, "internal", { note: "收回对客可见" }),
    successToast: "已收回对客可见 — AI 客服不再检索它",
    errorToast: "收回失败(未生效,可重试)",
    invalidateKeys: [["knowledge-documents"], ["kb-doc-review"]],
  });

  /**
   * 批量收回对客可见。逐份调 —— 量级本来就小(几十份),没必要为它开一个批量接口,
   * 而且逐份调失败是**部分失败**:哪几份没收回来要明确说出来,不能吞成一句"操作失败"
   * 让人以为全没生效(那会让人再点一次,把已经成功的又走一遍)。
   */
  const handleBatchRevoke = async () => {
    if (revocableSelected.length === 0) return;
    setRevoking(true);
    let ok = 0;
    const failed: number[] = [];
    for (const id of revocableSelected) {
      try {
        await kbDocReviewService.markVisibility(id, "internal", { note: "批量收回对客可见" });
        ok += 1;
      } catch {
        failed.push(id);
      }
    }
    setRevoking(false);
    setSelectedIds(new Set());
    queryClient.invalidateQueries({ queryKey: ["knowledge-documents"] });
    if (failed.length > 0) {
      toast({
        variant: "destructive",
        title: "部分收回失败",
        description: `已收回 ${ok} 份,失败 ${failed.length} 份 —— 失败的仍对客可见,请重试`,
      });
    } else {
      toast({
        title: "已收回对客可见",
        description: `${ok} 份内容已转为仅内部,AI 客服不再检索到它们`,
      });
    }
  };

  const totalFiltered = filteredDocs.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedDocs = filteredDocs.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // 文档总数减少把页码拉回 totalPages,避免空页(page 1-based → maxPage=totalPages)。
  // 加载中不 clamp,否则首屏 totalPages 默认 1 会把 URL 上的 ?page=N(浏览器回退恢复的)抹掉。
  // 搜索变化不需要这个兜底 — setKbSearch 自身已带 page: null。
  useClampedPage({ page, maxPage: totalPages, setPage, enabled: !documentsLoading });

  return (
    <>
      <TooltipProvider>
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-center gap-3 fx-enter">
            <div className="p-2.5 rounded-lg bg-primary/10">
              <BookOpen className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">{t("knowledgeBase.title")}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">{t("knowledgeBase.subtitle")}</p>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <Card className="shadow-sm fx-hover-lift" style={{ animationDelay: `0ms` }}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    <BookOpen className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-foreground">{totalDocs}</p>
                <p className="text-2xs text-muted-foreground mt-1">
                  {t("knowledgeBase.stats.totalDocs")}
                </p>
              </CardContent>
            </Card>
            <Card className="shadow-sm fx-hover-lift" style={{ animationDelay: `30ms` }}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 rounded-lg bg-success/10 text-success">
                    <Tag className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-foreground">{totalChunks}</p>
                <p className="text-2xs text-muted-foreground mt-1">
                  {t("knowledgeBase.stats.totalChunks")}
                </p>
              </CardContent>
            </Card>
            <Card className="shadow-sm fx-hover-lift" style={{ animationDelay: `60ms` }}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 rounded-lg bg-info/10 text-info">
                    <RefreshCw className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {syncedDocs}/{totalDocs}
                </p>
                <p className="text-2xs text-muted-foreground mt-1">
                  {t("knowledgeBase.stats.synced")}
                </p>
              </CardContent>
            </Card>
            <Card
              className={
                visibilityAlarming
                  ? "shadow-sm fx-hover-lift border-destructive"
                  : "shadow-sm fx-hover-lift"
              }
              style={{ animationDelay: `60ms` }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div
                    className={
                      visibilityAlarming
                        ? "p-2 rounded-lg bg-destructive/10 text-destructive"
                        : "p-2 rounded-lg bg-success/10 text-success"
                    }
                  >
                    <Eye className="h-4 w-4" />
                  </div>
                </div>
                <p
                  className={
                    visibilityAlarming
                      ? "text-2xl font-bold text-destructive"
                      : "text-2xl font-bold text-foreground"
                  }
                >
                  {visibilitySummary.visible}/{visibilitySummary.total}
                </p>
                <p className="text-2xs text-muted-foreground mt-1">AI 客服可见</p>
                {visibilityAlarming && (
                  <p className="text-2xs text-destructive mt-1">
                    客服检索不到任何内容,此刻等于没有知识库
                  </p>
                )}
              </CardContent>
            </Card>
            <Card className="shadow-sm fx-hover-lift" style={{ animationDelay: `90ms` }}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 rounded-lg bg-warning/10 text-warning">
                    <Filter className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {new Set(documents.flatMap((d) => d.tags ?? [])).size}
                </p>
                <p className="text-2xs text-muted-foreground mt-1">
                  {t("knowledgeBase.stats.tagCount")}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs: Documents + Personal Docs + Tag Management */}
          <Tabs defaultValue="documents">
            <TabsList>
              <TabsTrigger value="documents">{t("knowledgeBase.tabs.documents")}</TabsTrigger>
              {/* 2026-09-07 — 个人知识库一期:所有角色可见,是员工在这个页面唯一能写的地方
                  (documents tab 的上传口锁在 MANAGER+,见 kbUploadGate)。 */}
              {/* 1004 第三轮:代客中不出「个人文档」—— 代客身份传的个人文档会记在平台账号名下(api 也拒)。 */}
              {!isImpersonating && (
                <TabsTrigger value="personal">
                  {t("knowledgeBase.tabs.personal", { defaultValue: "个人文档" })}
                </TabsTrigger>
              )}
              <TabsTrigger value="tags">{t("knowledgeBase.tabs.tags")}</TabsTrigger>
              <TabsTrigger value="coverage">{t("knowledgeBase.tabs.coverage")}</TabsTrigger>
            </TabsList>

            <TabsContent value="documents" className="space-y-4 mt-4">
              {/* D6 — 租户提名态:告诉他"传了会怎样",而不是"你不能传" */}
              {uploadMode === "propose" && (
                <div
                  role="status"
                  className="flex items-start gap-3 rounded-lg border border-info/30 bg-info/5 p-3"
                >
                  <Info className="h-4 w-4 mt-0.5 text-info shrink-0" />
                  <div className="flex-1 text-xs text-foreground/80 leading-relaxed">
                    <p className="font-medium text-foreground">你上传的文档会先进待审</p>
                    <p className="mt-1">
                      为保证对外答复的质量,新文档经审核后才会被 AI 客服检索到 ——
                      {kbProposeReviewerHint(canGrantVisibility, reviewPolicy?.soloApprover)}
                      审核前它只在这里可见,不会被用来答客户。
                    </p>
                  </div>
                  {canGrantVisibility && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0 text-xs"
                      onClick={() => navigate("/kb/review")}
                    >
                      去审核台
                    </Button>
                  )}
                </div>
              )}
              {uploadMode === "none" && (
                <div
                  role="status"
                  className="flex items-start gap-3 rounded-lg border border-info/30 bg-info/5 p-3"
                >
                  <Info className="h-4 w-4 mt-0.5 text-info shrink-0" />
                  <div className="text-xs text-foreground/80 leading-relaxed">
                    <p className="font-medium text-foreground">你的角色可以查看,不能新增</p>
                    <p className="mt-1">新增知识库文档需要经理及以上权限。原文件可下载校验。</p>
                  </div>
                </div>
              )}
              {/* Toolbar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t("knowledgeBase.searchPlaceholder")}
                      className="pl-9 w-64"
                      value={kbSearch}
                      onChange={(e) => setKbSearch(e.target.value)}
                    />
                  </div>
                  <Select
                    value={visibilityFilter}
                    onValueChange={(v) => {
                      setVisibilityFilter(v as VisibilityFilter);
                      setSelectedIds(new Set());
                    }}
                  >
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(["all", "visible", "internal_only", "not_ready"] as VisibilityFilter[]).map(
                        (f) => (
                          <SelectItem key={f} value={f}>
                            {VISIBILITY_FILTER_LABEL[f]}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                  {revocableSelected.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={revoking}
                      onClick={handleBatchRevoke}
                    >
                      {revoking ? "收回中…" : `收回对客可见(${revocableSelected.length})`}
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      queryClient.invalidateQueries({ queryKey: ["knowledge-documents"] })
                    }
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    {t("knowledgeBase.refresh")}
                  </Button>
                  {/* 上传 = 提名口,MANAGER+ 就能开;删除 = 代客全权 + 提名者自治(自己未过审) */}
                  {canUpload && (
                    <>
                      <input
                        ref={uploadInputRef}
                        type="file"
                        multiple
                        accept=".txt,.pdf,.doc,.docx,.md"
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                      <Button size="sm" onClick={() => uploadInputRef.current?.click()}>
                        <Plus className="h-4 w-4 mr-1" />
                        {/* 文案跟着 mode 走:租户看见"代客上传文档"会当场懵 */}
                        {uploadButtonLabel}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Document Table */}
              <Card className="shadow-sm">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">
                        <span className="sr-only">选择</span>
                      </TableHead>
                      <TableHead>{t("knowledgeBase.table.filename")}</TableHead>
                      <TableHead>{t("knowledgeBase.table.chunkCount")}</TableHead>
                      <TableHead>{t("knowledgeBase.table.tags")}</TableHead>
                      <TableHead>{t("knowledgeBase.table.docType")}</TableHead>
                      <TableHead>{t("knowledgeBase.table.uploadedAt")}</TableHead>
                      <TableHead>{t("knowledgeBase.table.status")}</TableHead>
                      <TableHead>{t("knowledgeBase.table.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documentsLoading && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="text-center py-8 text-muted-foreground fx-skeleton"
                        >
                          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-1" />
                          {t("knowledgeBase.table.loading")}
                        </TableCell>
                      </TableRow>
                    )}
                    {!documentsLoading && filteredDocs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          {/* 2026-08-09:按上传门分支(此前误用 canManage=代客判定,能传的人
                              看到的是 D6 建门之前的「暂不开放自助上传」封条 —— 同屏三句话打架)。 */}
                          {canUpload
                            ? t("knowledgeBase.table.empty", { button: uploadButtonLabel })
                            : t("knowledgeBase.table.emptyReadonly", {
                                defaultValue:
                                  "暂无文档。仅经理及以上可上传;上传后经审核即可被 AI 客服引用。",
                              })}
                        </TableCell>
                      </TableRow>
                    )}
                    {pagedDocs.map((doc, i) => {
                      const statusType = KNOWLEDGE_STATUS_TYPES[doc.status] ?? "info";
                      return (
                        <TableRow
                          key={doc.id}
                          className="fx-enter"
                          style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
                        >
                          <TableCell>
                            {/* 只有当前对客可见的才可勾 —— 批量动作只做「收回」,
                                勾一份本来就是内部的没有意义(见 batchRevocableIds) */}
                            <Checkbox
                              checked={selectedIds.has(doc.id)}
                              disabled={doc.visibility !== "public"}
                              aria-label={`选择 ${doc.filename}`}
                              onCheckedChange={(v) => {
                                setSelectedIds((prev) => {
                                  const next = new Set(prev);
                                  if (v) next.add(doc.id);
                                  else next.delete(doc.id);
                                  return next;
                                });
                              }}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{doc.filename}</TableCell>
                          <TableCell>
                            {doc.chunkCount ?? 0}
                            {t("knowledgeBase.table.chunkSuffix")}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(doc.tags ?? []).length > 0 ? (
                                doc.tags.map((t) => (
                                  <Badge key={t} variant="secondary" className="text-2xs">
                                    {tagNameMap[t] ?? t}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {doc.docType === "qa" ? (
                              <Badge variant="secondary">{t("knowledgeBase.table.qaBadge")}</Badge>
                            ) : (
                              <Badge variant="outline">
                                {t("knowledgeBase.table.productBadge")}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {doc.createdAt
                              ? new Date(doc.createdAt).toLocaleString("zh-CN", { hour12: false })
                              : "-"}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap items-center gap-1">
                              <StatusBadge type={statusType}>{statusLabel(doc.status)}</StatusBadge>
                              {/* 治理轴与 ingest 轴是两回事:向量化跑完(已完成)不代表客服检索得到。
                                  不并排显示的话,租户看见"已完成"就以为能用了,而它其实等着人审。 */}
                              {doc.reviewStatus === "pending" && (
                                <StatusBadge type="warning">待审核</StatusBadge>
                              )}
                              {doc.reviewStatus === "rejected" && (
                                <StatusBadge type="error">已驳回</StatusBadge>
                              )}
                              {/* 第三根轴:审过了也可能只对内。不显示它 = 事故当天那样,
                                  租户看着"已完成 + 已审核"却怎么也想不通客服为什么答不出来。 */}
                              {doc.status === "done" &&
                                doc.reviewStatus === "approved" &&
                                doc.visibility !== "public" && (
                                  <StatusBadge type="warning">仅内部</StatusBadge>
                                )}
                              {/* B2 #21:「已对客」此前没有徽章 —— 三态(待审 / 仅内部 / 对客可见)缺了最重要的那一态,
                                  租户看不出哪些内容 AI 正拿去答真客户。 */}
                              {isCustomerVisible(doc) && (
                                <span data-kb-customer-visible className="inline-flex">
                                  <StatusBadge type="success">对客可见</StatusBadge>
                                </span>
                              )}
                              {/* 第四根轴(2026-09-16):公司内部谁看得到。与上面三根正交 ——
                                  一份文档可以「已审核 + 对客可见」同时「只给销售部」。
                                  全公司是默认档,不画徽章(画了满屏都是,反而看不见真收窄的那几份)。 */}
                              {doc.visibleScope && doc.visibleScope !== "ALL" && (
                                <StatusBadge type="info" data-kb-visible-scope>
                                  {VISIBLE_SCOPE_LABEL[doc.visibleScope]}
                                </StatusBadge>
                              )}
                            </div>
                            {/* B2 #24:INVISIBLE_REASON_LABEL 此前全仓无渲染点 —— 徽章说「待审核 / 仅内部」,
                                客服到底看不看得见要用户自己推。这里把原因一句话写在徽章下(失败行另有失败原因,不重复)。 */}
                            {doc.status !== "failed" && invisibleReason(doc) && (
                              <p
                                data-kb-invisible-reason
                                className="mt-1 max-w-[280px] text-2xs leading-snug text-muted-foreground"
                              >
                                {INVISIBLE_REASON_LABEL[invisibleReason(doc)!]}
                              </p>
                            )}
                            {/* 0816 反馈:只画「失败」徽章=让用户猜。0926:只画人话(原因码 → 人话,
                                存量原文/未知 → 兜底句),不再原样画 errorMsg(曾露内部地址)。 */}
                            {kbFailureText(doc) && (
                              <p
                                data-kb-error-msg
                                className="mt-1 max-w-[280px] text-2xs leading-snug text-destructive"
                              >
                                {kbFailureText(doc)}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {canChangeScope && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      aria-label="调整公司内可见范围"
                                      onClick={() => {
                                        setScopeEditing(doc);
                                        setEditScope(doc.visibleScope ?? "ALL");
                                        setEditDeptId(doc.visibleDeptId ?? null);
                                      }}
                                    >
                                      <Users className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>调整公司内可见范围</TooltipContent>
                                </Tooltip>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setViewingDoc(doc)}
                                aria-label={t("knowledgeBase.actions.view", {
                                  defaultValue: "查看详情",
                                })}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  {/* span 包裹是为了 disabled button 也能触发 tooltip hover */}
                                  <span className="inline-block">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      disabled={
                                        !(doc.canDownload ?? doc.originalAvailable) ||
                                        downloadMutation.isPending
                                      }
                                      aria-label={t("knowledgeBase.actions.download", {
                                        defaultValue: "下载原文件",
                                      })}
                                      onClick={() =>
                                        downloadMutation.mutate({
                                          id: doc.id,
                                          filename: doc.filename,
                                        })
                                      }
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {/* 三种灰:没有原件 / 企业关了一线下载 / 可下。分不清就会来问
                                      「为什么这个能下那个不能下」。 */}
                                  {doc.originalAvailable && doc.canDownload === false
                                    ? "本企业已关闭一线员工下载原件,可在详情里查看内容"
                                    : doc.originalAvailable
                                      ? t("knowledgeBase.actions.download", {
                                          defaultValue: "下载原文件",
                                        })
                                      : t("knowledgeBase.actions.downloadUnavailable", {
                                          defaultValue: "原文件不可用(早期上传的文档未保留原件)",
                                        })}
                                </TooltipContent>
                              </Tooltip>
                              {/* ★「设为对客可见」—— 这一行的黄标「仅内部」此前只能看不能改。
                                  不用 icon-only:放开对客是有后果的动作(AI 会拿它答真客户),
                                  一个眼睛图标说不清后果。 */}
                              {canGrantVisibility && canGrantCustomerVisible(doc) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8"
                                  disabled={grantVisibility.isPending}
                                  onClick={async () => {
                                    if (
                                      await confirm({
                                        title: `把《${doc.filename}》放开给 AI 客服?`,
                                        description: `放开后,AI 客服会拿这份内容去回答真客户。现在它只有${persona.title} / 陪练用得上。`,
                                        confirmText: "放开对客",
                                      })
                                    ) {
                                      grantVisibility.mutate(doc.id);
                                    }
                                  }}
                                >
                                  <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                                  设为对客可见
                                </Button>
                              )}
                              {/* B2 #21:行内「收回对客可见」(复用审批台同一个调用) */}
                              {canGrantVisibility && canRevokeCustomerVisible(doc) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8"
                                  disabled={revokeVisibilityOne.isPending}
                                  onClick={() => revokeVisibilityOne.mutate(doc.id)}
                                >
                                  <EyeOff className="mr-1 h-3.5 w-3.5" />
                                  收回对客可见
                                </Button>
                              )}
                              {/* K-2 提名者自治(2026-08-09):代客全权;租户 MANAGER+ 可删
                                  「自己上传且未过审」的文档(后端 owner+status 双守卫同边界)。
                                  已过审 = 对客生效资产,仍锁平台。 */}
                              {(canManage ||
                                (canUpload && doc.mine && doc.reviewStatus !== "approved")) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  disabled={deleteMutation.isPending}
                                  aria-label={t("knowledgeBase.actions.delete", {
                                    defaultValue: "删除文档",
                                  })}
                                  onClick={async () => {
                                    if (
                                      await confirm({
                                        title: t("knowledgeBase.delete.confirm", {
                                          filename: doc.filename,
                                        }),
                                        confirmText: t("common.delete", { defaultValue: "删除" }),
                                        variant: "destructive",
                                      })
                                    ) {
                                      deleteMutation.mutate(doc.id);
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {totalFiltered > 0 && (
                  <div className="flex items-center justify-between p-3 border-t">
                    <span className="text-xs text-muted-foreground">
                      {t("knowledgeBase.table.totalRecords", { count: totalFiltered })}
                    </span>
                    <Pagination
                      page={safePage - 1}
                      pageSize={PAGE_SIZE}
                      total={totalFiltered}
                      showTotal={false}
                      showJump={false}
                      onPageChange={(p) => setPage(p + 1)}
                    />
                  </div>
                )}
              </Card>
            </TabsContent>

            {!isImpersonating && (
              <TabsContent value="personal" className="mt-4">
                <Card className="shadow-sm p-4">
                  <PersonalDocsPanel />
                </Card>
              </TabsContent>
            )}

            <TabsContent value="tags" className="mt-4">
              <Card className="shadow-sm p-4">
                <TagManagementPanel />
              </Card>
            </TabsContent>

            <TabsContent value="coverage" className="mt-4">
              <Card className="shadow-sm p-4">
                <CoverageInsightPanel />
              </Card>
            </TabsContent>
          </Tabs>

          {/* 2026-09-16 — 调整公司内可见范围(与审批台的三个对客动作并列但正交) */}
          <Dialog open={scopeEditing !== null} onOpenChange={(v) => !v && setScopeEditing(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>调整公司内可见范围</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{scopeEditing?.filename}</p>
                <KbVisibleScopeField
                  scope={editScope}
                  deptId={editDeptId}
                  onChange={(sc, dept) => {
                    setEditScope(sc);
                    setEditDeptId(dept);
                  }}
                />
                <p className="text-2xs text-muted-foreground leading-relaxed">
                  这一项只管公司内部谁看得到;客户那一侧由「审核状态 / 对客可见」决定,两者互不影响。
                  改动会记入审计。
                </p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setScopeEditing(null)}>
                    取消
                  </Button>
                  <Button
                    disabled={editScope === "DEPT" && !editDeptId}
                    onClick={() => {
                      if (!scopeEditing) return;
                      scopeMutation.mutate({
                        id: scopeEditing.id,
                        scope: editScope,
                        deptId: editDeptId,
                      });
                    }}
                  >
                    保存
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* V27 — 代客上传 Dialog (tags 自动生成,无手填入口) */}
          <Dialog
            open={uploadDialogOpen}
            onOpenChange={(v) => {
              if (isUploading) return; // 上传中禁止关闭,避免中断串行队列。
              if (!v) {
                setUploadDialogOpen(false);
                setPendingFiles([]);
                setUploadScope("ALL");
                setUploadDeptId(null);
                uploadForm.reset({ tags: "", docType: "product", nodeCode: NODE_NONE });
              }
            }}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {/* B2 #26:与按钮同一句(此前按钮「(待审)」、弹窗「(提交后待审)」两处措辞) */}
                  {uploadButtonLabel}
                </DialogTitle>
              </DialogHeader>
              <Form {...uploadForm}>
                <form onSubmit={handleUploadConfirm} className="space-y-4">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label>{t("knowledgeBase.dialog.fileLabel")}</Label>
                      <span className="text-xs text-muted-foreground">
                        {t("knowledgeBase.dialog.fileCount", {
                          defaultValue: "{{count}} 个文件",
                          count: pendingFiles.length,
                        })}
                      </span>
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-1 rounded-md border border-border/60 p-1.5">
                      {pendingFiles.length === 0 ? (
                        <p className="px-1 py-1 text-xs text-muted-foreground">
                          {t("knowledgeBase.dialog.noFilesSelected", {
                            defaultValue: "未选择文件",
                          })}
                        </p>
                      ) : (
                        pendingFiles.map((f, i) => (
                          <div
                            key={`${f.name}-${i}`}
                            className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-muted/50"
                          >
                            <span className="truncate text-sm text-foreground" title={f.name}>
                              {f.name}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                              disabled={isUploading}
                              aria-label={t("knowledgeBase.dialog.removeFile", {
                                defaultValue: "移除文件",
                                filename: f.name,
                              })}
                              onClick={() => removePendingFile(i)}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <FormField
                    control={uploadForm.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem className="space-y-1.5">
                        <FormLabel>
                          {t("knowledgeBase.dialog.tagsLabel", { defaultValue: "标签" })}
                        </FormLabel>
                        <FormControl>
                          <TagSelector value={field.value} onChange={field.onChange} />
                        </FormControl>
                        <div className="flex items-start gap-2 rounded-md border border-info/30 bg-info/5 p-2.5">
                          <Info className="h-4 w-4 mt-0.5 text-info shrink-0" />
                          <p className="text-2xs text-muted-foreground leading-relaxed">
                            {t("knowledgeBase.dialog.tagsExistingOnlyNotice", {
                              defaultValue:
                                "只能从已注册的标签里挑选;上传时不会自动新建标签,需要新增请去「标签管理」。",
                            })}
                          </p>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <KbVisibleScopeField
                    scope={uploadScope}
                    deptId={uploadDeptId}
                    disabled={isUploading}
                    onChange={(sc, dept) => {
                      setUploadScope(sc);
                      setUploadDeptId(dept);
                    }}
                  />
                  <FormField
                    control={uploadForm.control}
                    name="docType"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <FormLabel>{t("knowledgeBase.dialog.docTypeLabel")}</FormLabel>
                        <FormControl>
                          <RadioGroup
                            value={field.value}
                            onValueChange={(v) => {
                              if (v === "product" || v === "qa") field.onChange(v);
                            }}
                            className="space-y-2"
                          >
                            <div className="flex items-start gap-2">
                              <RadioGroupItem value="product" id="dt-product" className="mt-1" />
                              <div className="flex-1">
                                <Label htmlFor="dt-product" className="cursor-pointer font-medium">
                                  {t("knowledgeBase.dialog.productLabel")}
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                  {t("knowledgeBase.dialog.productDesc")}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <RadioGroupItem value="qa" id="dt-qa" className="mt-1" />
                              <div className="flex-1">
                                <Label htmlFor="dt-qa" className="cursor-pointer font-medium">
                                  {t("knowledgeBase.dialog.qaLabel")}
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                  {t("knowledgeBase.dialog.qaDesc")}
                                </p>
                              </div>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* 2026-06-04 — 所属产品(可选), 通用 = 不绑产品树。 */}
                  <FormField
                    control={uploadForm.control}
                    name="nodeCode"
                    render={({ field }) => (
                      <FormItem className="space-y-1.5">
                        <FormLabel>
                          {t("knowledgeBase.dialog.productNodeLabel", {
                            defaultValue: "所属产品",
                          })}
                        </FormLabel>
                        <FormControl>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NODE_NONE}>
                                {t("knowledgeBase.dialog.productNodeNone", {
                                  defaultValue: "通用(不绑定产品)",
                                })}
                              </SelectItem>
                              {productNodes.map((n) => (
                                <SelectItem key={n.code} value={n.code}>
                                  {n.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isUploading}
                      onClick={() => {
                        setUploadDialogOpen(false);
                        setPendingFiles([]);
                        uploadForm.reset({ tags: "", docType: "product", nodeCode: NODE_NONE });
                      }}
                    >
                      {t("knowledgeBase.dialog.cancel")}
                    </Button>
                    <Button type="submit" disabled={isUploading || pendingFiles.length === 0}>
                      {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                      {isUploading
                        ? t("knowledgeBase.dialog.uploadingProgress", {
                            defaultValue: "上传中 {{done}}/{{total}} …",
                            done: (uploadProgress?.done ?? 0) + 1,
                            total: uploadProgress?.total ?? pendingFiles.length,
                          })
                        : t("knowledgeBase.dialog.confirmUpload")}
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>

          {/* Document Detail Dialog */}
          {viewingDoc && (
            <Dialog open={!!viewingDoc} onOpenChange={(v) => !v && setViewingDoc(null)}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{t("knowledgeBase.dialog.detailTitle")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-muted-foreground">
                        {t("knowledgeBase.dialog.fileNameLabel")}
                      </span>
                      {viewingDoc.filename}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        {t("knowledgeBase.dialog.docIdLabel")}
                      </span>
                      <span className="font-mono text-xs">{viewingDoc.docId}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        {t("knowledgeBase.dialog.statusLabel")}
                      </span>
                      {statusLabel(viewingDoc.status)}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        {t("knowledgeBase.dialog.chunkCountLabel")}
                      </span>
                      {viewingDoc.chunkCount ?? 0}
                      {t("knowledgeBase.table.chunkSuffix")}
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">
                        {t("knowledgeBase.dialog.tagsValueLabel")}
                      </span>
                      {(viewingDoc.tags ?? []).length > 0
                        ? viewingDoc.tags.map((tg) => tagNameMap[tg] ?? tg).join(", ")
                        : t("knowledgeBase.dialog.noTags")}
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">
                        {t("knowledgeBase.dialog.uploadTimeLabel")}
                      </span>
                      {viewingDoc.createdAt
                        ? new Date(viewingDoc.createdAt).toLocaleString("zh-CN", { hour12: false })
                        : "-"}
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </TooltipProvider>
    </>
  );
}
